import { ExternalLink, Flame, HeartHandshake, MapPin, ShieldCheck } from "lucide-react";
import {
    CHARITY_DONATE_URL,
    CHARITY_NAME,
    CHARITY_PCT,
    CHARITY_URL,
    FEE_SPLIT_TX,
    PUMPFUN_CREATOR_URL,
    PUMPFUN_URL,
    SHORTEN,
} from "../config";
import Mascot from "./Mascot";
import Reveal from "./Reveal";
import RewardsTracker from "./RewardsTracker";

const PROOF = [
    {
        label: "CREATOR REWARDS ON PUMP.FUN",
        sub: "LIVE SPLIT + TOTAL SOL DONATED, ON THE COIN PAGE",
        href: PUMPFUN_URL,
        testId: "proof-pumpfun-coin-link",
    },
    {
        label: "FEE-SPLIT TRANSACTION",
        sub: SHORTEN(FEE_SPLIT_TX.replace("https://solscan.io/tx/", ""), 10, 8),
        href: FEE_SPLIT_TX,
        testId: "proof-fee-split-tx-link",
    },
    {
        label: "BALDPIGEONDev PROFILE",
        sub: "PUMP.FUN CREATOR ACCOUNT",
        href: PUMPFUN_CREATOR_URL,
        testId: "proof-pumpfun-creator-link",
    },
];

const SPLIT = [
    { pct: "95%", label: "BALDPIGEONDev", sub: "CREATOR · FUNDS THE 25% BUYBACK & BURN", icon: Flame, tone: "orange" },
    { pct: `${CHARITY_PCT}%`, label: "PIGEON RESCUE", sub: `CHARITY · ${CHARITY_NAME.toUpperCase()}`, icon: HeartHandshake, tone: "cyan" },
];

const TONE = {
    orange: "border-orange-500/40 bg-orange-500/10 text-orange-300 shadow-[0_0_30px_rgba(255,61,0,0.15)]",
    cyan: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300 shadow-[0_0_30px_rgba(0,240,255,0.15)]",
};

export default function GivesBack() {
    return (
        <section id="gives-back" data-testid="gives-back-section" className="relative scroll-mt-24 overflow-hidden py-24 lg:py-32">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_50%_at_20%_50%,rgba(0,240,255,0.08),transparent)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
                <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
                    <div>
                        <Reveal>
                            <span className="kicker">REAL PIGEONS. REAL IMPACT.</span>
                            <h2 className="headline mt-4 text-4xl text-white sm:text-5xl lg:text-6xl">
                                BALD <span className="text-cyan-300">GIVES</span> BACK 🕊️
                            </h2>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-300 md:text-lg">
                                <p>
                                    <span className="font-bold text-white">{CHARITY_PCT}% of the Creator Rewards</span> this token
                                    earns on Pump.fun goes automatically to{" "}
                                    <a
                                        href={CHARITY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="gives-back-charity-inline-link"
                                        className="font-bold text-cyan-300 underline decoration-cyan-400/40 underline-offset-4 hover:text-cyan-100"
                                    >
                                        {CHARITY_NAME}
                                    </a>
                                    .
                                </p>
                                <p>
                                    A volunteer-run nonprofit in Seattle that rescues, rehabilitates and rehomes abandoned,
                                    sick and injured pigeons — the ones everybody else overlooks.
                                </p>
                                <p className="border-l-2 border-cyan-400/60 pl-4 font-bold text-cyan-100">
                                    "A bald pigeon looking out for the pigeons left behind."
                                </p>
                                <p className="text-sm text-slate-500">
                                    The split is locked into the token's Creator Rewards on Pump.fun and paid out automatically on-chain —
                                    no manual transfers, no promises to keep. Admin rights are revoked, so it can't be changed.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                                <a
                                    href={CHARITY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="gives-back-charity-button"
                                    className="btn-primary"
                                >
                                    VISIT {CHARITY_NAME.toUpperCase()} <ExternalLink className="h-4 w-4" />
                                </a>
                                <a
                                    href={CHARITY_DONATE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="gives-back-donate-button"
                                    className="btn-ghost"
                                >
                                    DONATE DIRECTLY <HeartHandshake className="h-4 w-4" />
                                </a>
                                <a
                                    href={PUMPFUN_CREATOR_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="gives-back-pumpfun-creator-button"
                                    className="btn-ghost"
                                >
                                    DEV ON PUMP.FUN <ExternalLink className="h-4 w-4" />
                                </a>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.15}>
                        <div className="relative flex flex-col items-center gap-6">
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(0,240,255,0.18),transparent_60%)]" />
                            <Mascot name="rescue" className="relative w-52 drop-shadow-[0_0_40px_rgba(0,240,255,0.25)] md:w-64" />
                            <div className="relative grid w-full grid-cols-2 gap-3" data-testid="rewards-split">
                                {SPLIT.map((s) => (
                                    <div
                                        key={s.label}
                                        data-testid={`rewards-split-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className={`glass flex flex-col gap-1 rounded-2xl border p-4 text-left ${TONE[s.tone]}`}
                                    >
                                        <s.icon className="h-5 w-5" />
                                        <span className="headline text-3xl leading-none md:text-4xl">{s.pct}</span>
                                        <span className="font-mono text-xs font-bold uppercase tracking-widest">{s.label}</span>
                                        <span className="font-mono text-[10px] uppercase leading-relaxed tracking-wider text-slate-400">{s.sub}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="relative flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                                <MapPin className="h-3 w-3 text-cyan-400/70" /> CREATOR REWARDS SPLIT · ENFORCED ON-CHAIN BY PUMP.FUN
                            </p>
                            <div
                                data-testid="fee-split-proof"
                                className="glass relative flex w-full flex-col gap-3 rounded-2xl border-cyan-400/20 p-4"
                            >
                                <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300">
                                    <ShieldCheck className="h-4 w-4" /> VERIFY IT YOURSELF
                                </div>
                                {PROOF.map((p) => (
                                    <a
                                        key={p.label}
                                        href={p.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={p.testId}
                                        className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-black/30 px-4 py-3 text-left transition-colors hover:border-cyan-400/40"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-mono text-xs font-bold uppercase tracking-widest text-slate-200">{p.label}</div>
                                            <div className="truncate font-mono text-[10px] text-slate-500">{p.sub}</div>
                                        </div>
                                        <ExternalLink className="h-4 w-4 shrink-0 text-cyan-300" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <RewardsTracker />
                </Reveal>
            </div>
        </section>
    );
}
